import type { ReactNode } from 'react';
import { cx } from '../cx';
import { devWarn } from '../dev';
import { LABEL_TEXT, VALUE_TEXT, chartFill, percentOf } from './scale';
import { NARROW_ONLY, WIDE_ONLY, type ChartLayout } from './layout';
import { ROUGH_FILL, SampleCount, sampleTier, type ThinLabels } from './thin';

export type BarChartRow = {
  id: string;
  label: ReactNode;
  value: number;
  /** Formatted value. */
  display: ReactNode;
  /**
   * When the bar is a median or average: how many values it rests on. Then 10 to 19 draws a faint
   * dashed bar marked rough, under 10 no bar, and every row shows its n. Leave out for counts.
   */
  sample?: number;
};

export type BarChartProps = {
  /** Sorted largest first unless the categories have a natural order. */
  rows: BarChartRow[];
  /** The one row the headline is about: sun, every other row context. Without it every row is ink. */
  highlightId?: string;
  /** The scale's end; defaults to the largest value. Bars always start at zero. */
  max?: number;
  /** The thin-data words, translated; needed when rows carry a `sample`. */
  thinLabels?: ThinLabels;
  /** One sentence that states the finding and the key values, for screen readers. */
  /** `auto` (default) switches to the narrow form in a narrow container: each label above its bar, the n beside the label. */
  layout?: ChartLayout;
  description: string;
  className?: string;
};

const ROW: Record<ChartLayout, string> = {
  auto: 'flex flex-col gap-0.5 @md:flex-row @md:items-center @md:gap-3',
  narrow: 'flex flex-col gap-0.5',
  wide: 'flex items-center gap-3',
};
const ROW_LABEL: Record<ChartLayout, string> = {
  auto: 'flex min-w-0 items-baseline gap-2 @md:w-1/4 @md:shrink-0',
  narrow: 'flex min-w-0 items-baseline gap-2',
  wide: 'flex w-1/4 min-w-0 shrink-0 items-baseline gap-2',
};
// In a column, flex-1 would collapse the track to no height: the narrow form sizes it by width.
const TRACK: Record<ChartLayout, string> = { auto: '@md:flex-1', narrow: '', wide: 'flex-1' };

/** Ranked horizontal bars for comparing categories. Values are written at the end of each bar. */
export function BarChart({
  rows,
  highlightId,
  max,
  thinLabels,
  layout = 'auto',
  description,
  className,
}: BarChartProps) {
  devWarn(rows.length > 12, `BarChart has ${rows.length} rows; show the top 10 and link to the full table.`);
  devWarn(
    rows.some((r) => r.value < 0),
    'BarChart takes counts and amounts from zero; use a Dumbbell or the diverging ramp for change.',
  );
  devWarn(
    rows.some((r) => r.sample !== undefined) && !thinLabels,
    'BarChart rows carry a sample; pass thinLabels so thin bars can say so.',
  );
  const end = max ?? Math.max(1, ...rows.filter((r) => r.sample === undefined || r.sample >= 10).map((r) => r.value));
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-2', className)}>
      {rows.map((r) => {
        const isHighlight = r.id === highlightId;
        const series = highlightId ? (isHighlight ? 'highlight' : 'context') : 'subject';
        const tier = r.sample === undefined ? 'full' : sampleTier(r.sample);
        return (
          <div key={r.id} className={ROW[layout]}>
            <div className={ROW_LABEL[layout]}>
              <span className={cx('truncate', LABEL_TEXT, isHighlight && 'font-semibold text-jc-ink')}>{r.label}</span>
              {r.sample !== undefined ? (
                <SampleCount n={r.sample} className={cx('shrink-0', NARROW_ONLY[layout])} />
              ) : null}
            </div>
            <span className={cx('flex min-w-0 items-center gap-2', TRACK[layout])}>
              {tier === 'none' ? (
                <span className="text-xs text-jc-ink-secondary">{thinLabels?.tooFew}</span>
              ) : (
                <>
                  <span
                    className={cx(
                      'block h-3.5 shrink-0 origin-left rounded-jc-info motion-safe:animate-jc-grow-x',
                      chartFill(series),
                      tier === 'rough' && ROUGH_FILL,
                    )}
                    style={{ width: `calc((100% - 4.5rem) * ${percentOf(r.value, 0, end) / 100})` }}
                  />
                  <span className={VALUE_TEXT}>
                    {r.display}
                    {tier === 'rough' ? <>, {thinLabels?.rough}</> : null}
                  </span>
                </>
              )}
            </span>
            {r.sample !== undefined ? (
              <SampleCount n={r.sample} className={cx('w-12 shrink-0 text-right', WIDE_ONLY[layout])} />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
